/* eslint-disable @typescript-eslint/no-non-null-assertion */
import './Quote.css';

import { useMutation } from '@apollo/client';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

import { AuthProvider, useAuth } from '../../auth/AuthProvider';
import { RatingQuotingClient } from '../../backend-client/ratingQuotingBackend';
import Loading from '../../components/Loading/Loading';
import {
  ApplicationInGroup,
  BusinessInfo,
  useGetApplicationGroupQuery,
  useGetBusinessInfoQuery
} from '../../generated/graphql';
import {
  GetQuoteExpired,
  GetQuoteOk,
  QuoteResponse
} from '../../generated/rating-quoting-graphql';
import PRUNE_INCOMPLETE_LOBS from '../../graphql/forms/pruneIncompleteLobs';
import UPDATE_LOB from '../../graphql/forms/updateLob';
import { clearApplicationEdit } from '../../utils/applicationEditSession';
import getEnv from '../../utils/getEnv';
import { Context, ContextValue } from '../../utils/store';
import { ReturnToQuotePopup } from './components/ReturnToQuotePopup/ReturnToQuotePopup';
import Quote from './Quote';
import {
  handleQuotePageRedirect,
  shouldNavigateToClientAgreement,
  useQuotePageRedirectQuery
} from './useQuotePageRedirect';

/**
 * Router state the quote page accepts. `arrivedFromSurvey` is set by LobShell's
 * redirect after a Phase-3 completion (NCP-5129).
 */
interface QuoteLocationState {
  arrivedFromSurvey?: boolean;
}

interface QuoteParams {
  /**
   * The id from the URL. Semantically this is an applicationGroupId under the
   * new URL scheme (/quote/:applicationGroupId), but legacy production links
   * pass an applicationId here. getApplicationGroup resolves either form.
   */
  applicationGroupId: string;
}

interface Option {
  text: string;
  value: string;
}

export interface CoveragesDataInput {
  name: string;
  chooseCoverage: boolean;
  finalPremium: number;
  limits: Record<string, number>;
  limitsOptions?: Record<string, Array<Option>>;
  /** E&O only: claims-made retroactive date, YYYY-MM-DD. */
  retroactiveDate?: string | null;
}

const { REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL } = getEnv();

/**
 * Props for inner component
 * Contains all pre-fetched data and validated props
 */

interface ApplicationQuoteGroup {
  lobType: string;
  applicationId: string;
  quoteResponse: QuoteResponse;
}

interface QuotePageContentProps {
  applicationGroupId: string;
  /** All applications in the resolved group, with their lobType. */
  /** Primary applicationId that drives the existing single-product quote flow. */
  primaryApplicationId: string;
  versionData: WorkflowVersion;
  /** Grouped application + quote response objects */
  applicationQuoteGroups: ApplicationQuoteGroup[];
  /** LOBs offered/created but not active — rendered as selectable "add" cards. */
  addableLobs: string[];
  businessInfoData: BusinessInfo;
  /** Add a LOB to the group (NCP-4545). */
  onAddLob?: (lobType: string) => void;
  /** Remove a LOB from the group (NCP-4545). */
  onDeselectLob?: (lobType: string) => void;
  refetchBusinessInfo?: () => void;
}

// Collect all successful quotes as an array of GetQuoteOk groups
type OkApplicationQuoteGroup = ApplicationQuoteGroup & {
  quoteData: GetQuoteOk;
};

// A product whose getQuote returned GetQuoteDecline. GetQuoteDecline carries no
// quote data, so we only forward the identifiers needed to render its label.
export type DeclinedApplicationQuoteGroup = {
  lobType: string;
  applicationId: string;
};

/**
 * QuotePageContent - Inner component rendered inside AuthProvider
 *
 * Responsibilities:
 * - Only renders when AuthProvider context is available
 * - Handles presentation logic (quote display, redirects, popups)
 * - Safely uses useAuth() hook for authentication
 * - Handles quote response types (OK, Expired)
 *
 * Note: All data is pre-validated by parent, so no need to re-fetch
 */
export const QuotePageContent: React.FC<QuotePageContentProps> = ({
  applicationGroupId,
  primaryApplicationId,
  versionData,
  applicationQuoteGroups,
  addableLobs,
  businessInfoData,
  onAddLob,
  onDeselectLob,
  refetchBusinessInfo
}) => {
  const context: ContextValue | null = useContext(Context);

  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const [isTnCLinkSent, setIsTnCLinkSent] = useState<boolean>(false);
  const [
    isRedirectCheckComplete,
    setIsRedirectCheckComplete
  ] = useState<boolean>(false);

  const isCustomerAgreementActive = context
    ? context.isCustomerAgreementActive
    : false;
  const setCustomerAgreementActive = context
    ? context.setCustomerAgreementActive
    : undefined;

  const {
    loading: isGetQuotePageRedirectLoading,
    error: getQuotePageRedirectError,
    data: getQuotePageRedirectData
  } = useQuotePageRedirectQuery(primaryApplicationId);

  const primaryQuoteResponse = applicationQuoteGroups?.find(
    (g) => g.applicationId === primaryApplicationId
  )?.quoteResponse;

  const okApplicationQuoteGroup: OkApplicationQuoteGroup[] =
    applicationQuoteGroups?.flatMap((g) =>
      g.quoteResponse?.__typename === 'GetQuoteOk'
        ? [{ ...g, quoteData: g.quoteResponse }]
        : []
    ) ?? [];

  // Products the backend declined while at least one other product is quotable.
  // These render as ineligible cards alongside the quotable coverages so the
  // customer can still buy the eligible product(s).
  const declinedApplicationQuoteGroups: DeclinedApplicationQuoteGroup[] =
    applicationQuoteGroups?.flatMap((g) =>
      g.quoteResponse?.__typename === 'GetQuoteDecline'
        ? [{ lobType: g.lobType, applicationId: g.applicationId }]
        : []
    ) ?? [];

  // An expired quote can belong to any product in the group, not just the
  // primary one — and a declined product short-circuits to GetQuoteDecline
  // before rating-quoting ever runs its expiry check, so inspecting a single
  // response used to miss the expiry entirely (NCP-5239). Scan them all.
  const returnToQuoteExpired = applicationQuoteGroups?.find(
    (g) => g.quoteResponse?.__typename === 'GetQuoteExpired'
  )?.quoteResponse as GetQuoteExpired | undefined;

  // Handle page redirects based on quote state
  // This effect checks if the user should be redirected away from this page
  // (e.g., to login or agreement page). It runs asynchronously via handleQuotePageRedirect.
  // We only mark isRedirectCheckComplete after the check finishes to ensure any
  // redirects happen before we render other UI (like ReturnToQuotePopup).
  useEffect(() => {
    // Wait for auth to resolve before deciding redirects. isAuthenticated is
    // false until the async /me fetch in AuthProvider completes; running the
    // redirect early treats a broker as a client and wrongly sends them to the
    // T&C page instead of the client agreement page.
    if (
      getQuotePageRedirectData &&
      !isGetQuotePageRedirectLoading &&
      !isAuthLoading
    ) {
      // Extract isTnCLinkSent from redirect data
      setIsTnCLinkSent(
        getQuotePageRedirectData.getQuotePageRedirect?.ownerData
          ?.isTnCLinkSent ?? false
      );

      // Check if user should be redirected and handle accordingly
      const localQuoteId =
        (primaryQuoteResponse &&
          (primaryQuoteResponse as GetQuoteOk | GetQuoteExpired).quoteId) ||
        undefined;

      handleQuotePageRedirect(
        getQuotePageRedirectData,
        localQuoteId,
        isAuthenticated,
        returnToQuoteExpired,
        primaryApplicationId,
        applicationGroupId,
        versionData
      ).then((shouldHaveRedirected) => {
        // CRITICAL: Only allow rendering if NO redirect is happening
        // If shouldHaveRedirected === true, never set isRedirectCheckComplete to true
        // This keeps the component in Loading state while redirect occurs
        if (!shouldHaveRedirected) {
          // Check if should navigate to Client & Agreement page
          const shouldNavigate = shouldNavigateToClientAgreement(
            getQuotePageRedirectData,
            isAuthenticated,
            returnToQuoteExpired
          );
          if (shouldNavigate && setCustomerAgreementActive) {
            setCustomerAgreementActive(true);
          }
          // Only mark as complete if NO redirect will happen
          setIsRedirectCheckComplete(true);
        }
        // If shouldHaveRedirected === true, we don't set the flag
        // The redirect will handle sending user away from this page
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getQuotePageRedirectData,
    isAuthenticated,
    isAuthLoading,
    returnToQuoteExpired,
    primaryApplicationId,
    isGetQuotePageRedirectLoading
  ]);

  if (getQuotePageRedirectError) {
    return (
      <div className="m-auto mt-20 error-bubble text-mobileFont md:text-base">
        Sorry! It is not you, it is us. We will get in touch with you to
        retrieve your quote.
      </div>
    );
  }

  if (isGetQuotePageRedirectLoading || isAuthLoading) {
    return <Loading />;
  }

  // Handle expired quote case
  // Only show the popup after redirect check is complete to ensure user isn't redirected away
  if (returnToQuoteExpired) {
    // Guard: don't render popup until we've finished checking for redirects
    if (!isRedirectCheckComplete) {
      return <Loading />;
    }

    const closePopup = () => {
      window.location.reload();
    };

    if (
      (returnToQuoteExpired?.isQuoteExpired ||
        returnToQuoteExpired?.isGracePeriodExpired) &&
      returnToQuoteExpired?.quoteExpireDate
    ) {
      return (
        <ReturnToQuotePopup
          isQuoteExpired={true}
          applicationGroupId={applicationGroupId}
          quoteExpireDate={returnToQuoteExpired.quoteExpireDate}
          closeAction={closePopup}
        />
      );
    } else if (
      returnToQuoteExpired?.isEffectiveDateExpired &&
      returnToQuoteExpired?.quoteExpireDate
    ) {
      return (
        <ReturnToQuotePopup
          isQuoteExpired={false}
          applicationGroupId={applicationGroupId}
          quoteExpireDate={returnToQuoteExpired.quoteExpireDate!}
          closeAction={closePopup}
        />
      );
    }
  }

  // At this point, ensure we have at least one successful quote
  const [firstOkGroup, ...restOkGroups] = okApplicationQuoteGroup;
  if (!firstOkGroup) {
    return <Loading />;
  }

  // Render active quote(s). `quoteData` is now an array of `GetQuoteOk`.
  // Key by the active applicationId set so the subtree remounts (re-seeding its
  // local quoteGroups state) when a LOB is added/removed via updateLob.
  return (
    <Quote
      key={applicationQuoteGroups.map((g) => g.applicationId).join(',')}
      applicationGroupId={applicationGroupId}
      versionData={versionData}
      applicationQuoteGroups={[firstOkGroup, ...restOkGroups]}
      declinedQuoteGroups={declinedApplicationQuoteGroups}
      addableLobs={addableLobs}
      onAddLob={onAddLob}
      onDeselectLob={onDeselectLob}
      canDeselectLob={applicationQuoteGroups.length > 1}
      shouldShowCustomerAgreement={isCustomerAgreementActive}
      isTnCLinkSent={isTnCLinkSent}
      businessInfo={businessInfoData}
      refetchBusinessInfo={refetchBusinessInfo}
    />
  );
};

/**
 * QuotePage - Container component that manages all quote page data fetching
 *
 * Flow:
 * 0. Resolve the URL id (applicationGroupId or legacy applicationId) via
 *    getApplicationGroup. This returns the canonical applicationGroupId and
 *    the list of child applications with their lobType.
 * 1. Use applications[0].applicationId (single-product flow today) to fetch
 *    version data from VersionControllerClient.
 * 2. Use version data to fetch quote from RatingQuotingClient
 * 3. Extract quoteId from quote response
 * 4. Fetch business info in parallel (via useGetBusinessInfoQuery)
 * 5. Once all data ready, conditionally render AuthProvider with quoteId
 * 6. AuthProvider validates auth context with the quoteId
 * 7. Only then render QuotePageContent (inner component)
 *
 * Key points:
 * - The URL param `:applicationGroupId` may actually be a legacy
 *   applicationId for back-compat with existing production links. The
 *   getApplicationGroup resolver accepts either and returns the group.
 * - AuthProvider only mounts when quoteId is available
 * - This prevents auth context from being created with undefined quoteId
 * - No timing race conditions for isAuthenticated value
 * - All errors handled before rendering children
 * - Progress set to 100% immediately on page load
 */
export const QuotePage: React.FC = () => {
  const { applicationGroupId: idFromUrl } = useParams<QuoteParams>();
  const location = useLocation<QuoteLocationState>();
  const locationState = location.state;
  const context: ContextValue | null = useContext(Context);

  // NCP-4588: capture the Quote-page time-to-render. `renderStart` is set on
  // first mount; `hasLoggedTimeToRender` guards the measurement so it is logged
  // exactly once per page load. This is measurement-only and does not affect
  // data fetching or rendering.
  const renderStart = useRef(performance.now());
  const hasLoggedTimeToRender = useRef(false);

  // Step 0: Resolve the URL id into an ApplicationGroup. This supports both
  // new-style /quote/:applicationGroupId URLs and legacy
  // /quote/:applicationId URLs; the backend resolver disambiguates.
  const {
    loading: isApplicationGroupLoading,
    error: applicationGroupError,
    data: applicationGroupData,
    refetch: refetchApplicationGroup
  } = useGetApplicationGroupQuery({
    variables: { id: idFromUrl },
    // Fetch the group from the network on every mount. Group membership changes
    // within a session: a LOB is added on the quote page (navigating to its
    // Phase-3 survey /<lobType>/<id> and back, remounting QuotePage) or removed,
    // and a LOB deselected/reselected on a Phase 2 re-visit is pulled from /
    // re-added to the group. The default cache-first policy would serve the
    // stale pre-change group here, so getQuote never fires for the new LOB and
    // stale coverages render.
    //
    // network-only (not cache-and-network): cache-and-network exposes the stale
    // cached group while the refetch is in flight, so the getQuote effect runs
    // once for the stale list (e.g. [EO]) and again for the fresh list
    // ([EO, GL]) — the quote page flashes EO-only, then GL appears. network-only
    // withholds data until the network response, so getQuote fires once for the
    // fresh list and both LOBs render together.
    fetchPolicy: 'network-only'
  });

  const applicationGroup = applicationGroupData?.getApplicationGroup;
  const applicationGroupId = applicationGroup?.applicationGroupId;
  const applications: ApplicationInGroup[] = useMemo(
    () => applicationGroup?.applications ?? [],
    [applicationGroup?.applications]
  );
  // LOBs offered/created but not currently active — rendered as "add" cards.
  const addableLobs: string[] = useMemo(
    () => applicationGroup?.addableLobs ?? [],
    [applicationGroup?.addableLobs]
  );
  // True while an updateLob call and its follow-up group refetch + re-quote are
  // in flight. Drives the same full-page <Loading /> screen updateCoverage
  // shows, so adding/removing a LOB doesn't flash intermediate card states.
  const [isUpdatingLob, setIsUpdatingLob] = useState(false);
  // NCP-5129: set when this round of quotes should be emailed even though it
  // produces no new quote, because the group's composition changed. Two sources:
  //
  //  - the customer added or removed a LOB on this page (set below); re-adding an
  //    already-rated LOB, or one that declines, yields only cache hits
  //  - the page was reached straight from a completed survey, flagged by
  //    LobShell's redirect. Adding a LOB that needs its Phase 3 survey navigates
  //    away and unmounts this page, so no in-page flag survives — the marker
  //    rides the router state instead.
  //
  // Consumed (and cleared) by the getQuote effect, which is the only place every
  // quote is known committed; sending straight after the updateLob mutation would
  // compose the subject before the new LOB has been rated and mislabel it as
  // declined.
  const lobChangePending = useRef(!!locationState?.arrivedFromSurvey);

  // Consume the marker once. Router state survives a reload, so leaving it in
  // place would re-send the email on every refresh of this URL. The ref above is
  // already seeded from it during the first render, so clearing here is safe.
  useEffect(() => {
    if (locationState?.arrivedFromSurvey) {
      history.replace(location.pathname, {});
    }
    // Mount-only: re-running on a later state change would clear a marker the
    // ref has not read yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The group's entry application (ApplicationGroup.applicationIds[0]). This is
  // NOT necessarily a purchasable product — underwriting can decline the LOB the
  // customer started with (NCP-5239) — so only use it for group-wide data. The
  // application that drives the page is `primaryApplicationId` below.
  const entryApplicationId = applications[0]?.applicationId;

  // NCP-4545: add/remove a LOB from the quote page via the updateLob mutation.
  const history = useHistory();
  const [updateLobMutation] = useMutation<
    {
      updateLob: {
        applicationGroupId: string;
        redirectApplicationId?: string | null;
      };
    },
    { applicationGroupId: string; lobType: string; selected: boolean }
  >(UPDATE_LOB);

  // NCP-5260: cleanup of never-completed members, run on arrival (see the
  // effect below).
  const [pruneIncompleteLobsMutation] = useMutation<
    {
      pruneIncompleteLobs: {
        applicationGroupId: string;
        removedApplicationIds: string[];
        removedLobTypes: string[];
      };
    },
    { applicationGroupId: string }
  >(PRUNE_INCOMPLETE_LOBS);

  // Add a LOB: re-add a previously-created one (refetch re-runs getQuote so it
  // shows priced) or create a new one and route to its Phase-3 survey first.
  const onAddLob = useCallback(
    async (lobType: string) => {
      if (!applicationGroupId) return;
      setIsUpdatingLob(true);
      try {
        const { data } = await updateLobMutation({
          variables: { applicationGroupId, lobType, selected: true }
        });
        const redirectApplicationId = data?.updateLob?.redirectApplicationId;
        if (redirectApplicationId) {
          // Edit Identity: updateLob opened an 'add' walk for this LOB's
          // survey. Drop any walk id this tab still holds (an edit left
          // unfinished) so the survey joins the new walk rather than reading
          // as replaced.
          clearApplicationEdit();
          // Navigating to the LOB's Phase-3 survey; keep the loading screen up
          // until this page unmounts.
          history.push(`/${lobType}/${redirectApplicationId}`);
          return;
        }
        lobChangePending.current = true;
        await refetchApplicationGroup();
        // isUpdatingLob is cleared by the getQuote effect once the re-quote for
        // the new applications resolves (covers the getQuote lag after refetch).
      } catch (err) {
        setIsUpdatingLob(false);
        throw err;
      }
    },
    [applicationGroupId, updateLobMutation, history, refetchApplicationGroup]
  );

  // Remove a LOB. The backend rejects removing the last remaining LOB; the UI
  // only offers the control when more than one LOB is active. Shows the loading
  // screen while the removal + re-quote is in flight.
  const onDeselectLob = useCallback(
    async (lobType: string) => {
      if (!applicationGroupId) return;
      setIsUpdatingLob(true);
      try {
        await updateLobMutation({
          variables: { applicationGroupId, lobType, selected: false }
        });
        lobChangePending.current = true;
        await refetchApplicationGroup();
        // isUpdatingLob is cleared by the getQuote effect once the re-quote
        // resolves.
      } catch (err) {
        setIsUpdatingLob(false);
        throw err;
      }
    },
    [applicationGroupId, updateLobMutation, refetchApplicationGroup]
  );

  // Step 1: Initialize state for fetched data
  const [versionData, setVersionData] = useState<WorkflowVersion>();
  const [versionDataError, setVersionDataError] = useState<unknown>();

  const [quoteDataFromRatingQuoting, setQuoteDataFromRatingQuoting] = useState<
    Array<{
      lobType: string;
      applicationId: string;
      quoteResponse: QuoteResponse;
    }>
  >();
  const [
    quoteDataFromRatingQuotingError,
    setQuoteDataFromRatingQuotingError
  ] = useState<unknown>();

  // Fetch business info in parallel. Skipped until we have resolved the
  // applicationId from the applicationGroup so we don't fire the query with
  // an undefined variable.
  const {
    loading: isBusinessInfoLoading,
    error: businessInfoError,
    data: businessInfoData,
    refetch: refetchBusinessInfo
  } = useGetBusinessInfoQuery({
    variables: { applicationId: entryApplicationId ?? '' },
    skip: !entryApplicationId,
    // network-only for the same reason getApplicationGroup above is: the
    // applicant can leave this page for the application, change their business
    // name / email / effective date, and come back. The query and its variables
    // are unchanged, so cache-first served the pre-edit answers and the quote
    // page showed a stale business name. refetchBusinessInfo only covers edits
    // made from THIS page's own popups, not a round-trip through Phase 2.
    fetchPolicy: 'network-only'
  });

  // (quoteId extraction removed) Quote IDs will be derived from per-application
  // responses by the inner component where needed.

  // Step 1: Fetch version info from Version Controller, keyed off the URL
  // applicationGroupId via getVersionFromApplicationGroup. This runs in
  // parallel with getApplicationGroup (both depend only on the URL id) instead
  // of waiting for the group to resolve a primary applicationId, so the
  // critical path becomes max(applicationGroup, version) -> quote rather than
  // applicationGroup -> version -> quote. The version includes the backend
  // URLs needed by the RatingQuoting and payment clients.
  useEffect(() => {
    if (!idFromUrl) return;
    const versionControllerClient = new VersionControllerClient(
      REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
    );
    versionControllerClient
      .getVersionFromApplicationGroup(idFromUrl)
      .then((data) => {
        setVersionData(data);
      })
      .catch((err) => {
        setVersionDataError(err);
      });
  }, [idFromUrl]);

  // The rating quote is the only fetch gated on the resolved workflow version.
  const effectiveQuoteBackendVersion = versionData?.quoteBackendVersion;

  // NCP-5260: drop members whose Phase 3 survey was never completed, before
  // anything tries to quote them.
  //
  // Adding a LOB here pushes its Application into the group and navigates to
  // that survey; abandoning it leaves the group claiming a coverage with no
  // answers, which failed the WHOLE page — the sibling LOB's valid quote
  // included. This is the only place that invariant holds, so the cleanup lives
  // here rather than in a query every phase shares.
  //
  // Gates the getQuote fan-out below so it never runs against the stale list.
  // Best-effort: a failure lets the page proceed exactly as it did before
  // rather than blocking on cleanup.
  const [isIncompleteLobsPruned, setIsIncompleteLobsPruned] = useState(false);
  useEffect(() => {
    if (!applicationGroupId || isIncompleteLobsPruned) return;
    let cancelled = false;
    pruneIncompleteLobsMutation({ variables: { applicationGroupId } })
      .then(async ({ data }) => {
        if (cancelled) return;
        // Only refetch when something actually went — otherwise this costs an
        // extra round-trip on every arrival for nothing.
        if (data?.pruneIncompleteLobs?.removedApplicationIds?.length) {
          await refetchApplicationGroup();
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsIncompleteLobsPruned(true);
      });
    return () => {
      cancelled = true;
    };
  }, [
    applicationGroupId,
    isIncompleteLobsPruned,
    pruneIncompleteLobsMutation,
    refetchApplicationGroup
  ]);

  // Step 2: Fetch quote data from Rating & Quoting backend.
  // Runs as soon as we have an applications list and a quoteBackendVersion
  // (from the applicationGroup or, as a fallback, the version controller).
  // We fetch in parallel with businessInfo and the version fetch.
  useEffect(() => {
    if (
      !isIncompleteLobsPruned ||
      !applications ||
      applications.length === 0 ||
      !effectiveQuoteBackendVersion
    )
      return;
    const ratingQuotingClient = new RatingQuotingClient(
      effectiveQuoteBackendVersion
    );

    // Fetch getQuote for each application in parallel and store responses keyed by lobType + applicationId
    Promise.all(
      applications.map((app) =>
        ratingQuotingClient.getQuote(app.applicationId).then((data) => ({
          lobType: app.lobType,
          applicationId: app.applicationId,
          quoteResponse: data
        }))
      )
    )
      .then((results) => {
        setQuoteDataFromRatingQuoting(results);
        // NCP-5129: one consolidated Foxden email per group, triggered here
        // rather than inside getQuote. This is the only point at which every
        // LOB's quote is committed, so it is the only point at which the subject
        // can name every quote number — the getQuote calls above race each other.
        //
        // Gated on isNewQuote so the email tracks group composition: adding a
        // LOB produces a new quote and so a second email covering both LOBs,
        // while a plain page refresh produces neither.
        const hasNewQuote = results.some(
          ({ quoteResponse }) =>
            quoteResponse.__typename === 'GetQuoteOk' &&
            quoteResponse.isNewQuote
        );
        const lobChanged = lobChangePending.current;
        if ((hasNewQuote || lobChanged) && applicationGroupId) {
          // Cleared before the send so a later remount of this page cannot
          // re-fire on a stale composition change.
          lobChangePending.current = false;
          // Fire-and-forget: an internal sales notification must never block the
          // quote page or surface an error to the customer.
          ratingQuotingClient
            .sendApplicationGroupEmail(applicationGroupId)
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.error('[NCP-5129] group email trigger failed', err);
            });
        }
      })
      .catch((err) => setQuoteDataFromRatingQuotingError(err))
      // Clear the LOB-update loading screen once the re-quote for the new
      // applications set has resolved (see onAddLob/onDeselectLob).
      .finally(() => setIsUpdatingLob(false));
  }, [
    applications,
    effectiveQuoteBackendVersion,
    applicationGroupId,
    isIncompleteLobsPruned
  ]);

  // Step 4: Set progress to 100% immediately on page load
  // We don't wait for data fetches to complete - the progress bar shows completion
  // as soon as the user navigates to this page. Context dependency ensures this
  // runs once per context instance (once on mount).
  useEffect(() => {
    if (context) {
      context.setProgress(100);
    }
  }, [context]);

  // NCP-4588: the Quote content first renders once the ApplicationGroup has
  // resolved and all data needed by QuotePageContent is present (mirrors the
  // loading guards below). This is read-only — it derives from existing state
  // and does not change any fetch or render behaviour.
  const primaryQuoteResponseForTiming = quoteDataFromRatingQuoting?.find(
    (r) => r.applicationId === entryApplicationId
  )?.quoteResponse;
  const isQuoteReady =
    !isApplicationGroupLoading &&
    !!applicationGroupId &&
    idFromUrl === applicationGroupId &&
    !!entryApplicationId &&
    !!versionData &&
    !!quoteDataFromRatingQuoting &&
    !!primaryQuoteResponseForTiming &&
    !isBusinessInfoLoading &&
    !!businessInfoData;

  // NCP-4588: log the time-to-render once, on the loading -> rendered
  // transition, so we can measure frontend Quote-page load time.
  useEffect(() => {
    if (isQuoteReady && !hasLoggedTimeToRender.current) {
      hasLoggedTimeToRender.current = true;
      // eslint-disable-next-line no-console
      console.info('[timing] quotePage.timeToRender', {
        ms: Math.round(performance.now() - renderStart.current)
      });
    }
  }, [isQuoteReady]);

  // Step 5: Error guard - check if any of the four fetches failed
  // If applicationGroup, version, quote, or business info fetch failed,
  // show error to user. This prevents invalid/undefined data from being
  // passed to child components.
  if (
    applicationGroupError ||
    versionDataError ||
    quoteDataFromRatingQuotingError ||
    businessInfoError
  ) {
    return (
      <div className="m-auto mt-20 error-bubble text-mobileFont md:text-base">
        Sorry! It is not you, it is us. We will get in touch with you to
        retrieve your quote.
      </div>
    );
  }

  // Step 6a: Wait for the ApplicationGroup query to resolve before doing
  // anything that depends on applicationGroupId — including AuthProvider, which
  // sends it as a header to /me. After this guard, applicationGroupId is
  // narrowed to `string`.
  if (isApplicationGroupLoading || !applicationGroupId) {
    return <Loading />;
  }

  // NCP-4545: while a LOB is being added/removed, show the same loading screen
  // as updateCoverage until the group refetch + re-quote settle — avoids
  // flashing intermediate card states (checked "available" card, then the
  // quote card).
  if (isUpdatingLob) {
    return <Loading />;
  }

  // Step 6b: Redirect legacy /quote/:applicationId URLs to the canonical
  // /quote/:applicationGroupId. This normalises bookmarks, shared links, and
  // browser history so the URL always reflects the true applicationGroupId.
  if (idFromUrl !== applicationGroupId) {
    return <Redirect to={`/quote/${applicationGroupId}`} />;
  }

  // Step 6c: Wait for remaining fetches needed by QuotePageContent.
  // Ensure the entry application's quote response has arrived.
  const entryQuoteResponse = quoteDataFromRatingQuoting?.find(
    (r) => r.applicationId === entryApplicationId
  )?.quoteResponse;

  if (
    !entryApplicationId ||
    !versionData ||
    !quoteDataFromRatingQuoting ||
    !entryQuoteResponse ||
    isBusinessInfoLoading ||
    !businessInfoData
  ) {
    return <Loading />;
  }

  // Step 7a: Handle declined quotes.
  // Only redirect to the completion page when EVERY product in the group was
  // declined (rate, don't quote) — there is nothing for the customer to buy.
  // If at least one product is quotable, we stay on the quote page and render
  // an ineligible card for each declined product (handled in QuotePageContent).
  const allQuotesDeclined = quoteDataFromRatingQuoting.every(
    (r) => r.quoteResponse?.__typename === 'GetQuoteDecline'
  );
  if (allQuotesDeclined) {
    return <Redirect to="/complete" />;
  }

  // Step 8: Render the page with authenticated context
  // All data fetches are complete and quoteId is available. AuthProvider wraps
  // QuotePageContent to make useAuth() safe to call in child and nested components.
  // Pass only the extracted data to child - businessInfoData.getBusinessInfo extracts
  // the typed object from the GraphQL query response.
  //
  // Render active cards from quote data intersected with the authoritative
  // group membership (applications) — quoteDataFromRatingQuoting comes from a
  // separate getQuote round-trip that can briefly lag getApplicationGroup.
  // Never render a LOB as addable while it's still an active quote card
  // (addableLobs, from the group query, updates a beat before the re-quote).
  const activeAppIds = new Set(applications.map((a) => a.applicationId));
  const activeQuoteGroups = quoteDataFromRatingQuoting.filter((g) =>
    activeAppIds.has(g.applicationId)
  );
  const activeLobTypes = new Set(activeQuoteGroups.map((g) => g.lobType));
  const visibleAddableLobs = addableLobs.filter(
    (lobType) => !activeLobTypes.has(lobType)
  );

  // NCP-5239: the application that drives the page is the first one that
  // actually quoted, NOT the group's entry application. Underwriting can
  // decline the entry LOB, and a declined application has no quote, no invoice,
  // no policy and no ApplicationOwner — so keying the redirect check on it
  // reports "not sent, not signed, not paid" for the whole group and the
  // customer is never forwarded to the T&C or thank-you page. Quote.tsx already
  // picks the first quotable group for the purchase flow; this keeps the two in
  // agreement. Falls back to the entry app when nothing quoted (e.g. every
  // quote expired), which is the pre-existing behaviour.
  const primaryApplicationId =
    activeQuoteGroups.find((g) => g.quoteResponse?.__typename === 'GetQuoteOk')
      ?.applicationId ?? entryApplicationId;

  return (
    <AuthProvider applicationGroupId={applicationGroupId}>
      <QuotePageContent
        applicationGroupId={applicationGroupId}
        primaryApplicationId={primaryApplicationId}
        applicationQuoteGroups={activeQuoteGroups}
        addableLobs={visibleAddableLobs}
        versionData={versionData}
        businessInfoData={businessInfoData.getBusinessInfo}
        onAddLob={onAddLob}
        onDeselectLob={onDeselectLob}
        refetchBusinessInfo={refetchBusinessInfo}
      />
    </AuthProvider>
  );
};

export default QuotePage;
